/*
 * The pictures behind Hidden Picture's tiles (ADR 0133 §2): one 20 × 20 pixel-art
 * picture per season or holiday, one character per pixel, read top row first.
 * The board lays a 5 × 5 grid of tiles over it, so each tile hides a 4 × 4 block.
 *
 * Kept as strings, like the color-by-number collie (ADR 0058 §1), so a picture
 * can be read and redrawn in the source. Each picture's palette maps its own
 * characters to fills; "." is always the background. The fills are the art's own
 * colours and do not follow the site theme in dark mode.
 */

export interface PixelArt {
    /** Character → CSS colour. */
    palette: Record<string, string>;
    /** 20 strings of 20 characters each. */
    rows: string[];
}

export const ART_SIZE = 20;

const PUMPKIN: PixelArt = {
    palette: {
        ".": "#312e81",
        "O": "#f97316",
        "D": "#c2410c",
        "G": "#15803d",
        "Y": "#fde047",
        "W": "#f8fafc",
    },
    rows: [
        "....................",
        ".WW.......GG........",
        ".WW......GG.........",
        ".....OOODGGDOOO.....",
        "...OOOOODOODOOOOO...",
        "..OOOOOODOODOOOOOO..",
        ".OOOOOOOOOOOOOOOOOO.",
        ".OOOOYOOOOOOOOYOOOO.",
        ".OOOYYYOOOOOOYYYOOO.",
        ".OOYYYYOOOOOOYYYYOO.",
        ".DOOOOOOOYYOOOOOOOD.",
        ".DOOOOOOYYYYOOOOOOD.",
        ".DOYOOOOOOOOOOOOYOD.",
        ".DOYYYYOYYYYOYYYYOD.",
        ".DOOYYYYYYYYYYYYOOD.",
        "..DOOYYYYOOYYYYOOD..",
        "..OOOOOOOOOOOOOOOO..",
        "...OOOOODOODOOOOO...",
        ".....OOODOODOOO.....",
        "....................",
    ],
};

const TURKEY: PixelArt = {
    palette: {
        ".": "#fef3c7",
        "R": "#dc2626",
        "O": "#f97316",
        "Y": "#facc15",
        "N": "#78350f",
        "L": "#a16207",
        "K": "#111827",
        "W": "#ffffff",
        "B": "#f59e0b",
        "G": "#65a30d",
    },
    rows: [
        "....................",
        ".....RRRRRRRRRR.....",
        "...RRROOOOOOOORRR...",
        "..RROOOYYYYYYOOORR..",
        ".RROOYYYLLLLYYYOORR.",
        ".ROOYYYLWKLLLYYYOOR.",
        "RROOYYYLLLLBBYYYOORR",
        "ROOYYYYLLLLBYYYYYOOR",
        "ROOYYYNLRRLNYYYYYOOR",
        "ROOYYNNNRRNNNYYYYOOR",
        ".ROYNNNNRNNNNNYYYOR.",
        "..RYNNNNNNNNNNNYYR..",
        "....NNNNNNNNNNNN....",
        "....NNNNNNNNNNNN....",
        ".....NNNNNNNNNN.....",
        "......NNNNNNNN......",
        ".......B....B.......",
        ".......B....B.......",
        "GGGGGGBBBGGBBBGGGGGG",
        "GGGGGGGGGGGGGGGGGGGG",
    ],
};

const GIFT: PixelArt = {
    palette: {
        ".": "#1e3a8a",
        "W": "#f8fafc",
        "R": "#dc2626",
        "D": "#991b1b",
        "Y": "#facc15",
        "A": "#ca8a04",
    },
    rows: [
        ".W.......W.......W..",
        "......W......W......",
        "..W.YYY....YYY...W..",
        "...YYAYY..YYAYY.....",
        "W..YAAAYYYYAAAY...W.",
        "....YYAAYYAAYY......",
        ".....YYYYYYYY.......",
        "..RRRRRRRYYRRRRRRR..",
        "..RRRRRRRYYRRRRRRR..",
        "..DDDDDDDYYDDDDDDD..",
        "...RRRRRRYYRRRRRR...",
        "...RRRRRRYYRRRRRR.W.",
        "...RRRRRRYYRRRRRR...",
        "...YYYYYYYYYYYYYY...",
        "...YYYYYYYYYYYYYY...",
        "...RRRRRRYYRRRRRR...",
        "...RRRRRRYYRRRRRR...",
        "...RRRRRRYYRRRRRR...",
        "...DDDDDDYYDDDDDD...",
        "WWWWWWWWWWWWWWWWWWWW",
    ],
};

const SNOWMAN: PixelArt = {
    palette: {
        ".": "#7dd3fc",
        "W": "#ffffff",
        "S": "#cbd5e1",
        "K": "#111827",
        "O": "#f97316",
        "R": "#dc2626",
        "N": "#78350f",
    },
    rows: [
        "..W.........W.....W.",
        "......KKKKKKK.......",
        "W.....KKKKKKK....W..",
        "......KKKKKKK.......",
        "....KKKKKKKKKKK.....",
        ".......WWWWWW.....W.",
        "......WWKWWKWW......",
        "..W...WWWWOOOO......",
        "......WWKKKKWW......",
        ".......WWWWWW.......",
        ".....RRRRRRRRRR.....",
        "N...WWWRRWWWWWWW...N",
        ".N.WWWWRRWKWWWWWW.N.",
        "..NWWWWRRWWWWWWWWN..",
        "...WWWWWWWKWWWWWW...",
        "...WWWWWWWWWWWWWS...",
        "....WWWWWWKWWWWWS...",
        "....SWWWWWWWWWWSS...",
        "WWWWWSSWWWWWWSSWWWWW",
        "WWWWWWWWWWWWWWWWWWWW",
    ],
};

const HEART: PixelArt = {
    palette: {
        ".": "#fce7f3",
        "R": "#e11d48",
        "D": "#9f1239",
        "W": "#ffffff",
        "P": "#f9a8d4",
    },
    rows: [
        "....................",
        "..P..............P..",
        ".....RRRR..RRRR.....",
        "...RRRRRRRRRRRRRR...",
        "..RRWWRRRRRRRRRRRR..",
        ".RRWWRRRRRRRRRRRRRR.",
        ".RRWRRRRRRRRRRRRRRR.",
        ".RRRRRRRRRRRRRRRRRD.",
        ".RRRRRRRRRRRRRRRRRD.",
        ".RRRRRRRRRRRRRRRRDD.",
        "..RRRRRRRRRRRRRRRD..",
        "..RRRRRRRRRRRRRRDD..",
        "...RRRRRRRRRRRRDD...",
        "....RRRRRRRRRRDD....",
        ".....RRRRRRRRDD.....",
        "......RRRRRRDD......",
        ".......RRRRDD.......",
        "........RRDD........",
        ".P.......DD......P..",
        "....................",
    ],
};

const SHAMROCK: PixelArt = {
    palette: {
        ".": "#fef9c3",
        "G": "#16a34a",
        "L": "#4ade80",
        "D": "#166534",
        "Y": "#eab308",
    },
    rows: [
        "....................",
        ".......GGG.GGG......",
        "......GGGGGGGGG.....",
        "......GLLGGGGGG.....",
        "......GLGGGGGGG.....",
        ".GGG...GGGGGGG..GGG.",
        "GGGGGG..GGGGG.GGGGGG",
        "GLLGGGG..GGG.GGGGGGG",
        "GLGGGGGG..D.GGGGGGGG",
        ".GGGGGGGGGDGGGGGGGG.",
        ".GGGGGGGGGDGGGGGGGG.",
        "GGGGGGGG.DD.GGGGGGGG",
        "GGGGGGG..D...GGGGGGG",
        "GGGGGG...D....GGGGGG",
        ".GGG....D......GGG..",
        "........D...........",
        ".......D............",
        "......D.............",
        "....Y.....Y......Y..",
        "....................",
    ],
};

const TULIP: PixelArt = {
    palette: {
        ".": "#bae6fd",
        "P": "#ec4899",
        "D": "#be185d",
        "L": "#f9a8d4",
        "G": "#16a34a",
        "E": "#15803d",
        "Y": "#fde047",
    },
    rows: [
        "YY..................",
        "YY..................",
        "....................",
        "......P...P...P.....",
        ".....PPP.PPP.PPP....",
        ".....PLPPPPPPPPP....",
        ".....PLPPPPDPPPP....",
        ".....PPPPPPDPPPP....",
        "......PPPPDDPPP.....",
        "......PPPPDPPPP.....",
        ".......PPPDPPP......",
        ".........GG.........",
        "..E......GG.........",
        "..EE.....GG.....E...",
        "...EE....GG...EEE...",
        "...EEE...GG..EEE....",
        "....EEE..GG.EEE.....",
        ".....EEE.GGEEE......",
        "GGGGGGEEEGGEEGGGGGGG",
        "GGGGGGGGGGGGGGGGGGGG",
    ],
};

const ICECREAM: PixelArt = {
    palette: {
        ".": "#fef08a",
        "P": "#f472b6",
        "M": "#6ee7b7",
        "W": "#ffffff",
        "C": "#d97706",
        "T": "#fbbf24",
        "R": "#dc2626",
        "K": "#78350f",
    },
    rows: [
        "....................",
        ".........RR.........",
        "........RRRR........",
        ".......PPRRPP.......",
        "......PPPPPPPP......",
        ".....PWPPPPPPPP.....",
        ".....PWPPPPPPPP.....",
        ".....PPPPPPPPPP.....",
        "....MMMMPPPPMMMM....",
        "....MWMMMMMMMMMM....",
        "....MMMMMMMMMMMM....",
        ".....MMMMMMMMMM.....",
        ".....TCTTCTTCTTT....",
        "......CTTCTTCTT.....",
        "......TCTTCTTCT.....",
        ".......CTTCTTC......",
        ".......TCTTCTT......",
        "........CTTCT.......",
        ".........TCT........",
        "..........T.........",
    ],
};

const APPLE: PixelArt = {
    palette: {
        ".": "#dcfce7",
        "R": "#dc2626",
        "D": "#991b1b",
        "W": "#fecaca",
        "N": "#78350f",
        "G": "#16a34a",
    },
    rows: [
        "....................",
        ".........N..GGG.....",
        ".........N.GGGGG....",
        "........NNGGGG......",
        "....RRRR.N..RRRR....",
        "..RRRRRRRNRRRRRRRR..",
        ".RRWWRRRRRRRRRRRRRR.",
        ".RWWRRRRRRRRRRRRRRR.",
        ".RWRRRRRRRRRRRRRRRD.",
        ".RRRRRRRRRRRRRRRRRD.",
        ".RRRRRRRRRRRRRRRRRD.",
        ".RRRRRRRRRRRRRRRRDD.",
        ".RRRRRRRRRRRRRRRRDD.",
        "..RRRRRRRRRRRRRRDD..",
        "..RRRRRRRRRRRRRRDD..",
        "...RRRRRRRRRRRRDD...",
        "....RRRRRDRRRRDD....",
        ".....RRRD..DRRD.....",
        "....................",
        "....................",
    ],
};

export type PictureId =
    | "snowman"
    | "heart"
    | "shamrock"
    | "tulip"
    | "icecream"
    | "apple"
    | "pumpkin"
    | "turkey"
    | "gift";

export const PICTURE_ART: Record<PictureId, PixelArt> = {
    snowman: SNOWMAN,
    heart: HEART,
    shamrock: SHAMROCK,
    tulip: TULIP,
    icecream: ICECREAM,
    apple: APPLE,
    pumpkin: PUMPKIN,
    turkey: TURKEY,
    gift: GIFT,
};
